import classNames from 'classnames';
import { AnimationControls, motion, TargetAndTransition, Transition } from 'framer-motion';
import React, { memo, Suspense, useCallback, useMemo } from 'react';

import { AvailableForWithdrawal, Header, Logo, Menu, Tooltip, UserTier } from 'common/components';
import SidebarComponent from 'common/components/Sidebar/Sidebar.component';

import { useBowser, useI18n, useTracking } from 'common/hooks';
import svgIcons from 'common/svgIcons';

import AuthButtonsComponent from 'components/AuthButtons/AuthButtons.component';

import { getClassNameFromRoute } from 'services/className.service';

import { useTheme } from 'blocks/ThemeProvider';

import SvgIconUrl from '../../common/components/SvgIconUrl/SvgIconUrl.component';
import './index.css';

const Balance = React.lazy(
  () => import(/* webpackChunkName: "Balance" */ 'common/components/Balance/Balance.component'),
);

const ActionButtons = React.lazy(
  () => import(/* webpackChunkName: "ActionButtons" */ 'views/home/components/ActionButtons/ActionButtons.component'),
);

type Props = {
  isAuthenticated?: boolean;
};

const MainLayout: React.FC<React.PropsWithChildren<Props>> = ({ children, isAuthenticated }) => {
  const { translate } = useI18n();
  const { isMobile } = useBowser();
  const { layout, updateLayout } = useTheme();
  const { track } = useTracking();

  const className = classNames({
    'main-layout': true,
    'main-layout--mobile': isMobile,
    'main-layout--desktop': !isMobile,
    'main-layout--desktop-collapsible': !isMobile && layout.sidebar.compact,
    [`main-layout__${getClassNameFromRoute()}`]: true,
  });

  const leftColumnClassNames = classNames({
    'column column-left': true,
    'column-left--mini': layout.sidebar.compact,
  });

  const expandButtonClassNames = classNames({
    'main__layout-expand-button-icon': true,
    'main__layout-expand-button-icon--collapsible': layout.sidebar.compact,
  });

  const renderCallToRegister = useCallback(
    () => (
      <div className='header-desk'>
        <h2 data-test='header-title-register' className='header-title header-desk__title'>
          {translate('mobile:header:register:title')}
        </h2>
        <p data-test='header-description-register' className='page-text header-desk__description'>
          {translate('mobile:header:register:description')}
        </p>
        <AuthButtonsComponent className='header-desk__buttons' />
      </div>
    ),
    [translate],
  );

  const expandButtonTooltip = useCallback(
    (text: string) => <span className='Body2_reg_14'>{translate.toText(text)}</span>,
    [translate],
  );

  const onToggleSidebar = useCallback(() => {
    track(layout.sidebar.compact ? 'layout:sidebar:expand' : 'layout:sidebar:collapse');
    updateLayout('sidebar.compact', !layout.sidebar.compact);
  }, [layout.sidebar.compact, track, updateLayout]);

  const renderSidebarToggle = useCallback(
    () => (
      <div
        className={classNames('main__layout-expand-button', {
          'main__layout-expand-button--collapsible': layout.sidebar.compact,
        })}
      >
        <Tooltip
          enabled
          overlay={expandButtonTooltip(layout.sidebar.compact ? 'mobile:sidebar:expand' : 'mobile:sidebar:collapse')}
          placement='right'
        >
          <button data-test='sidebar-toggle' type='button' className={expandButtonClassNames} onClick={onToggleSidebar}>
            <SvgIconUrl symbol={svgIcons.ExpandIcon()} />
          </button>
        </Tooltip>
      </div>
    ),
    [expandButtonClassNames, expandButtonTooltip, layout.sidebar.compact, onToggleSidebar],
  );

  const sidebarTransition: Transition | undefined = useMemo(() => ({ duration: 0.3, times: [0, 0.5, 1] }), []);
  const sidebarAnimate: boolean | AnimationControls | TargetAndTransition | undefined = useMemo(
    () => ({ opacity: [0, 0, 1] }),
    [],
  );

  const logoIcon = useMemo(() => {
    if (layout.sidebar.compact) return svgIcons.LogoMini();

    return undefined;
  }, [layout.sidebar.compact]);

  const renderDesktopLayout = useCallback(() => {
    if (!isMobile) {
      return (
        <div className={leftColumnClassNames}>
          <Header className={layout.sidebar.compact ? 'header--collapsible' : ''}>
            <div className='header-top'>
              <Logo icon={logoIcon} />
            </div>
            <React.Suspense fallback={null}>
              {!layout.sidebar.compact && <Balance />}
              {!isAuthenticated && !layout.sidebar.compact && renderCallToRegister()}
            </React.Suspense>
            {!layout.sidebar.compact && <AvailableForWithdrawal />}
          </Header>
          <div className='panel panel--inner-shadow no-padding flex-grow-1 overflow-hidden'>
            <motion.div
              layout
              data-collapsed={layout.sidebar.compact}
              data-test='main-layout-aside'
              key={String(layout.sidebar.compact)}
              transition={sidebarTransition}
              animate={sidebarAnimate}
              initial={false}
              className='flex flex-column no-padding flex-grow-1 overflow-hidden'
            >
              <Suspense>
                <ActionButtons />
              </Suspense>
              <UserTier />
              <SidebarComponent />
            </motion.div>
            {renderSidebarToggle()}
          </div>
        </div>
      );
    }
    return null;
  }, [
    isMobile,
    leftColumnClassNames,
    layout.sidebar.compact,
    logoIcon,
    isAuthenticated,
    renderCallToRegister,
    sidebarTransition,
    sidebarAnimate,
    renderSidebarToggle,
  ]);

  return (
    <>
      {/* {isMobile && <PullToRefresh />} TODO: polishing and release in the next sprint; */}
      <div className={className}>
        {renderDesktopLayout()}
        <div className='column' data-test='main-layout-main'>
          {children}
        </div>
      </div>
      <Menu />
    </>
  );
};

export default memo(MainLayout);
